import { useCallback, useState } from "react";
import useTaskSort from "./useTaskSort";
import useTaskSearchAutocomplete from "./useTaskSearchAutocomplete";
import useTaskListQuery from "./useTaskListQuery";

// Sort, search/chips, and the list query are each their own hook - this just
// wires them together, since all three need to agree on one thing: a sort or
// search change drops any in-progress "jump to this task" anchor, because a
// new order or a narrower list makes that anchor meaningless.
const useModelTaskScreenController = ({ modelId, anchorTaskId }) => {
	const [navigationAnchorId, setNavigationAnchorId] = useState(anchorTaskId);
	const resetAnchor = useCallback(() => setNavigationAnchorId(null), []);
	// Lets a caller (eg. task creation) jump straight to a task regardless of
	// where it falls under the current sort/filter/pagination, by reusing the
	// same anchored-fetch path a URL deep link takes.
	const navigateToTask = useCallback((taskId) => setNavigationAnchorId(taskId), []);

	const { sorting, activeSort, setSorting } = useTaskSort({ onSortChange: resetAnchor });
	const {
		search,
		setSearch,
		chips,
		chipParams,
		handleSelectSuggestion,
		removeChip,
		autocompleteData,
		isSearchingSuggestions,
	} = useTaskSearchAutocomplete({ modelId, onSearchChange: resetAnchor });
	const { rows, totalCount, refresh, query } = useTaskListQuery({
		modelId,
		sortField: activeSort.id,
		sortDesc: activeSort.desc,
		chipParams,
		navigationAnchorId,
	});

	return {
		rows,
		totalCount,
		sorting,
		setSorting,
		search,
		setSearch,
		chips,
		handleSelectSuggestion,
		removeChip,
		autocompleteData,
		isSearchingSuggestions,
		refresh,
		query,
		navigationAnchorId,
		navigateToTask,
	};
};

export default useModelTaskScreenController;
